import React, { useState } from 'react';
import CourseRow from '../components/CourseRow';
import useCourses from '../hooks/UseCourses';
import AddCourseForm from '../components/AddCourseForm';

const CourseEditor = () => {
    const { courses, loading, addCourse, deleteCourse, editCourse, reloadData } = useCourses();
    const [isEditing, setIsEditing] = useState(false);
    const [courseId, setCourseId] = useState(0);
    const [courseName, setCourseName] = useState('');
    const [courseLink, setCourseLink] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [linkFilter, setLinkFilter] = useState('');
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    React.useEffect(() => {
        setCourseId(courses.length + 1);
    }, [courses]);

    const filterByName = (data, filter) => {
        return data.filter(course => {
            const name = (course.text || '').trim().toLowerCase();
            return name.includes(filter.trim().toLowerCase());
        });
    };

    const filterByLink = (data, filter) => {
        return data.filter(course => {
            const link = (course.url || '').trim().toLowerCase();
            return link.includes(filter.trim().toLowerCase());
        });
    };

    const sortData = (data, column, direction) => {
        return [...data].sort((a, b) => {
            if (!column) return 0;

            let aValue, bValue;

            if (column === 'text') {
                aValue = a.text || '';
                bValue = b.text || '';
            } else if (column === 'url') {
                aValue = a.url || '';
                bValue = b.url || '';
            } else if (column === 'id') {
                aValue = a.id;
                bValue = b.id;
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const filteredCourses = filterByLink(
        filterByName(courses, nameFilter),
        linkFilter
    );

    const sortedAndFilteredCourses = sortData(filteredCourses, sortColumn, sortDirection);

    const handleAddOrUpdateCourse = async () => {
        try {
            const courseData = { 
                id: isEditing ? courseId : courses.length + 1,
                name: courseName,
                link: courseLink 
            };
    
            if (isEditing) {
                await editCourse(courseData);
            } else {
                await addCourse(courseData);
            }
            resetForm();
            reloadData();
        } catch (error) {
            alert("Не удалось сохранить курс: " + error.message);
        }
    };

    const resetFilters = () => {
        setNameFilter('');
        setLinkFilter('');
    };

    const handleEditCourse = (course) => {
        setIsEditing(true);
        setCourseName(course.text);
        setCourseLink(course.url);
        setCourseId(course.id);
    };

    const handleDeleteCourse = (course) => {
        if (window.confirm("Вы уверены, что хотите удалить этот курс?")) {
            deleteCourse(course.id)
                .then(() => reloadData())
                .catch(err => {
                    console.error("Ошибка при удалении курса:", err);
                    alert("Не удалось удалить курс: " + err.message);
                });
        }
    };

    const resetForm = () => {
        setIsEditing(false);
        setCourseName('');
        setCourseLink('');
    };

    if (loading) return (
        <div className="loading">
            <p>Загрузка данных...</p>
        </div>
    );

    return (
        <div className="course-editor-container">
            <h2>Редактор курсов</h2>

            <div className="controls">
                <div className="filter-group">
                    <label htmlFor="name-filter">
                        Название курса:
                        <input
                            id="name-filter"
                            type="text"
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            placeholder="Фильтр по названию"
                        />
                    </label>

                    <label htmlFor="link-filter">
                        Ссылка на курс:
                        <input
                            id="link-filter"
                            type="text"
                            value={linkFilter}
                            onChange={(e) => setLinkFilter(e.target.value)}
                            placeholder="Фильтр по ссылке"
                        />
                    </label>
                </div>

                <div className="action-buttons">
                    <button onClick={resetFilters} className="reset-btn">
                        Сбросить фильтры
                    </button>
                    <button 
                        onClick={reloadData} 
                        className="refresh-btn"
                    >
                        Обновить данные
                    </button>
                </div>
            </div>

            <div className="form-container">
                <AddCourseForm 
                    courseName={courseName} 
                    setCourseName={setCourseName} 
                    courseLink={courseLink} 
                    setCourseLink={setCourseLink} 
                    onAddOrUpdateCourse={handleAddOrUpdateCourse}
                    isEditing={isEditing}
                    onCancel={resetForm}
                />
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('id')}>
                                № {sortColumn === 'id' && (
                                    sortDirection === 'asc' ? '↑' : '↓'
                                )}
                            </th>
                            <th onClick={() => handleSort('text')}>
                                Название курса {sortColumn === 'text' && (
                                    sortDirection === 'asc' ? '↑' : '↓'
                                )}
                            </th>
                            <th onClick={() => handleSort('url')}>
                                Ссылка на курс {sortColumn === 'url' && (
                                    sortDirection === 'asc' ? '↑' : '↓'
                                )}
                            </th>
                            <th colSpan="2">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedAndFilteredCourses.length > 0 ? (
                            sortedAndFilteredCourses.map((course, index) => (
                                <CourseRow
                                    key={course.id}
                                    course={course}
                                    editCourse={() => handleEditCourse(course)}
                                    deleteCourse={() => handleDeleteCourse(course)}
                                    courseNumber={index + 1}
                                />
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="no-data">
                                    Нет курсов для отображения
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <style>{`
                .course-editor-container {
                    padding: 20px;
                    font-family: Arial, sans-serif;
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                h2 {
                    margin-bottom: 20px;
                    color: #333;
                }
                
                .controls {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 15px;
                    background: #f8f9fa;
                    padding: 15px;
                    border-radius: 8px;
                }
                
                .filter-group {
                    display: flex;
                    gap: 15px;
                    flex-wrap: wrap;
                    align-items: flex-end;
                }
                
                .filter-group label {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    font-size: 14px;
                }
                
                .filter-group input {
                    padding: 8px 12px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    font-size: 14px;
                    min-width: 200px;
                }
                
                .action-buttons {
                    display: flex;
                    gap: 10px;
                    align-items: flex-end;
                }
                
                button {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 14px;
                }
                
                .reset-btn {
                    background-color: #6c757d;
                    color: white;
                }
                
                .refresh-btn {
                    background-color: #17a2b8;
                    color: white;
                }
                
                button:hover {
                    opacity: 0.9;
                    transform: translateY(-1px);
                }
                
                .form-container {
                    background: #f8f9fa;
                    padding: 20px;
                    border-radius: 8px;
                    margin-bottom: 20px; /* Добавлен отступ снизу */
                }
                
                .table-container {
                    overflow-x: auto;
                    border-radius: 8px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.05);
                    margin-top: 20px; /* Добавлен отступ сверху */
                }
                
                table {
                    width: 100%;
                    border-collapse: collapse;
                    background: white;
                }
                
                th, td {
                    padding: 12px 15px;
                    text-align: left;
                    border-bottom: 1px solid #dee2e6;
                }
                
                th {
                    background-color: #f8f9fa;
                    cursor: pointer;
                    position: relative;
                    font-weight: 600;
                    color: #495057;
                }
                
                th:hover {
                    background-color: #e9ecef;
                }
                
                tr:hover {
                    background-color: #f8f9fa;
                }
                
                .no-data {
                    text-align: center;
                    color: #6c757d;
                    padding: 20px;
                }
                
                .loading {
                    padding: 20px;
                    text-align: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin: 20px 0;
                }
                
                @media (max-width: 768px) {
                    .controls {
                        flex-direction: column;
                    }
                    
                    .action-buttons {
                        justify-content: flex-end;
                    }
                    
                    .filter-group label {
                        width: 100%;
                    }
                    
                    table {
                        display: block;
                        overflow-x: auto;
                    }
                }
            `}</style>
        </div>
    );
};


export default CourseEditor;